import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { Budget, Transaction } from '../types/budget';
import { formatCurrency } from '../utils/budget';

interface DeleteTransactionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  transaction?: Transaction;
  budget?: Budget;
}

export const DeleteTransactionDialog: React.FC<DeleteTransactionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  transaction,
  budget,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

        <div className="relative mx-auto w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-surface-800">
          <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-white">
            {t('transaction.delete')}
          </Dialog.Title>

          <div className="mt-2">
            <p className="text-sm text-slate-500 dark:text-slate-300">{t('common.confirm')}</p>
            {transaction && (
              <div className="mt-3 rounded-md bg-slate-50 p-4 dark:bg-surface-700">
                <p className="mb-2 text-base font-medium text-slate-800 dark:text-slate-200">
                  {transaction.description}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('transaction.category')}:
                    </span>{' '}
                    <span className="text-slate-700 dark:text-slate-300">{transaction.category}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('transaction.responsible')}:
                    </span>{' '}
                    <span className="text-slate-700 dark:text-slate-300">
                      {transaction.responsible}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('transaction.destination')}:
                    </span>{' '}
                    <span className="text-slate-700 dark:text-slate-300">
                      {transaction.destination}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">
                      {t('transaction.amount')}:
                    </span>{' '}
                    <span
                      className={`font-medium ${
                        transaction.amount >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-rose-600 dark:text-rose-400'
                      }`}
                    >
                      {formatCurrency(transaction.amount, budget?.settings?.currency)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); onConfirm(); onClose(); }} className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:bg-surface-700 dark:text-slate-200 dark:hover:bg-surface-600"
              onClick={onClose}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 dark:bg-rose-500 dark:hover:bg-rose-600"
            >
              {t('common.delete')}
            </button>
          </form>
        </div>
      </div>
    </Dialog>
  );
};
